'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useRBAC } from '@/contexts/rbac-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  DealerDto,
  PetrohqApiResponseListDealerDto,
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';
import {
  AMT_ROUND_CODES,
  AMT_ROUND_LABELS,
  CNTR_MGMT_CODES,
  CNTR_MGMT_LABELS,
  GEN2_STORE_CODES,
  GEN2_STORE_LABELS,
  INVOICE_PRINT_TYPE_CODES,
  INVOICE_PRINT_TYPE_LABELS,
  INVOICE_PRINT_TYPE2_CODES,
  INVOICE_PRINT_TYPE2_LABELS,
  ISSUE_TERM_CODES,
  ISSUE_TERM_LABELS,
  POS_QTY_ROUND_CODES,
  POS_QTY_ROUND_LABELS,
  PRINT_TYPE_CODES,
  PRINT_TYPE_LABELS,
  RECEIPT_ISSUE_CODES,
  RECEIPT_ISSUE_LABELS,
  SUPPLY_AMT_CALC_CODES,
  SUPPLY_AMT_CALC_LABELS,
  TRANS_METHOD_CODES,
  TRANS_METHOD_LABELS,
  USE_GEO_ECODE_CODES,
  USE_GEO_ECODE_LABELS,
  isInvoiceSettingField,
  saveInvoiceSetting,
} from './dealer-invoice-setting';

export function CustManagement() {
  const { toast } = useToast();
  const [dealers, setDealers] = useState<DealerDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // 필터 상태 가져오기
  const { store } = useFilterStore();
  const { getAllStores } = useOrganization();
  const { canAccessMenu } = useRBAC();

  // 서버 권한(menu_api_path → cust-invoice-setting)과 같은 키로 판정한다.
  // 이 키는 관리자 역할세트에만 부여되므로 일반 사용자에게는 그리드가 읽기 전용이다.
  const canEditInvoiceSetting = canAccessMenu('cust-invoice-setting');

  const fetchDealers = useCallback(async () => {
    setIsLoading(true);
    try {
      // 필터에서 선택된 store 값들을 사용, 없으면 모든 store 사용
      const storeCodeList = store.length > 0 ? store : getAllStores();

      const searchParams = new URLSearchParams();
      storeCodeList.forEach(storeCode => {
        searchParams.append('storeCodeList', storeCode);
      });

      const response = await apiClient.get('master/cust', {
        searchParams: searchParams,
      });
      const data = (await response.json()) as PetrohqApiResponseListDealerDto;
      const dealerData = data.result || [];
      setDealers(dealerData);
    } catch {
      toast({
        title: '조회 실패',
        description: '거래처 데이터를 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [store, toast, getAllStores]);

  /**
   * 셀 편집 확정 → 그 필드 하나만 저장. 실패하면 이전 값으로 되돌리고 서버 문구를 보여준다.
   *
   * 필드 이름은 getOrgFieldName 으로 읽는다. getFieldName 은 대문자로 정규화된 이름
   * (INVOICEISSUETERM)을 돌려줘서 매칭이 늘 실패하고, 저장이 조용히 건너뛰어진다.
   * 값은 이벤트가 주는 newValue 를 쓴다 - 이 시점의 데이터소스는 아직 이전 값이다.
   */
  const handleEditRowChanged = useCallback(
    (
      grid: { getDataSource: () => any },
      _itemIndex: number,
      row: number,
      field: number,
      oldValue: unknown,
      newValue: unknown
    ) => {
      const dataSource = grid.getDataSource();
      const fieldName = dataSource.getOrgFieldName(field);
      if (!isInvoiceSettingField(fieldName)) return;

      const { storeCd, dealCode } = dataSource.getJsonRow(row);
      const value =
        newValue === undefined || newValue === null || newValue === ''
          ? null
          : String(newValue);

      void saveInvoiceSetting(storeCd, dealCode, fieldName, value).catch(
        (error: unknown) => {
          dataSource.setValue(row, field, oldValue);
          toast({
            title: '저장 실패',
            description:
              error instanceof Error ? error.message : '값을 저장하지 못했습니다',
            variant: 'destructive',
          });
        }
      );
    },
    [toast]
  );

  // 초기 데이터 로드
  useEffect(() => {
    fetchDealers();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchDealers();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('cust')} />
      }
      title='거래처 목록'
      additionalActions={
        !canEditInvoiceSetting && (
          <Badge variant='outline' className='text-muted-foreground'>
            읽기 전용
          </Badge>
        )
      }
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 거래처 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              // 발행설정 컬럼만 편집 대상이다. 나머지 컬럼은 각자 editable={false} 로 잠근다.
              edit: {
                readOnly: !canEditInvoiceSetting,
                editable: canEditInvoiceSetting,
                commitByCell: true,
              },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={dealers}
            // 기본 정렬은 매장별 거래처번호 오름차순이다. 화면에 보이는 매장 컬럼(storeName)으로
            // 묶어야 정렬 결과와 표시가 어긋나지 않는다. 사용자가 헤더로 다시 정렬할 수 있다.
            onInitialized={grid =>
              grid.orderBy(
                ['storeName', 'dealCode'],
                [RealGrid.SortDirection.ASCENDING, RealGrid.SortDirection.ASCENDING]
              )
            }
            onEditRowChanged={handleEditRowChanged}
          >
            <RGDataField
              fieldName={'compCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'compName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'dealCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'dealName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'geokind'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'invoiceIssueTerm'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'receiptIssueType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'useGeoEcode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'statementPrintType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'invoicePrintType1'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'invoicePrintType2'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'transMethod'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'gen2StoreYn'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'deptCd'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'cntrMgmtType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'posQtyRoundType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'amtRoundType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'supplyAmtCalcType'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'email'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'emailRecipient'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataColumn
              fieldName={'storeName'}
              editable={false}
              header={{ text: '매장' }}
            />
            <RGDataColumn
              fieldName={'dealCode'}
              editable={false}
              header={{ text: '거래처번호' }}
              width={70}
            />
            <RGDataColumn
              fieldName={'dealName'}
              editable={false}
              header={{ text: '거래처명' }}
            />
            <RGDataColumn
              fieldName={'geokind'}
              editable={false}
              header={{ text: '거래처종류' }}
              lookupDisplay={true}
              values={['00', '01', '02', '03', '04', '05', '06']}
              labels={[
                '일반',
                '은행',
                '카드사',
                '정유사',
                '배달',
                '보관단체',
                '보관개별',
              ]}
            />
            {/* 아래 6개가 세금계산서 발행 동작을 결정한다. 관리자만 편집 가능하다.
                발행기간유형은 기간의 길이가 아니라 마감 기준일이다(정본 ISSUE_TERM_NM). */}
            <RGDataColumn
              fieldName={'invoiceIssueTerm'}
              header={{ text: '발행기간유형' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={ISSUE_TERM_CODES}
              labels={ISSUE_TERM_LABELS}
            />
            <RGDataColumn
              fieldName={'receiptIssueType'}
              header={{ text: '영수증발행유형' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={RECEIPT_ISSUE_CODES}
              labels={RECEIPT_ISSUE_LABELS}
            />
            <RGDataColumn
              fieldName={'useGeoEcode'}
              header={{ text: '확장코드 사용' }}
              width={110}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={USE_GEO_ECODE_CODES}
              labels={USE_GEO_ECODE_LABELS}
            />
            <RGDataColumn
              fieldName={'statementPrintType'}
              header={{ text: '명세서 인쇄유형' }}
              width={190}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={PRINT_TYPE_CODES}
              labels={PRINT_TYPE_LABELS}
            />
            <RGDataColumn
              fieldName={'invoicePrintType1'}
              header={{ text: '계산서 인쇄유형1' }}
              width={130}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={INVOICE_PRINT_TYPE_CODES}
              labels={INVOICE_PRINT_TYPE_LABELS}
            />
            <RGDataColumn
              fieldName={'invoicePrintType2'}
              header={{ text: '계산서 인쇄유형2' }}
              width={150}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={INVOICE_PRINT_TYPE2_CODES}
              labels={INVOICE_PRINT_TYPE2_LABELS}
            />
            {/* 아래는 V47 로 추가된 나머지 컬럼이다. 편집 권한은 발행설정과 같은 키를 쓴다. */}
            <RGDataColumn
              fieldName={'supplyAmtCalcType'}
              header={{ text: '공급가 산출방식' }}
              width={150}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={SUPPLY_AMT_CALC_CODES}
              labels={SUPPLY_AMT_CALC_LABELS}
            />
            <RGDataColumn
              fieldName={'transMethod'}
              header={{ text: '수송방법' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={TRANS_METHOD_CODES}
              labels={TRANS_METHOD_LABELS}
            />
            <RGDataColumn
              fieldName={'gen2StoreYn'}
              header={{ text: '일반2보관' }}
              width={110}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={GEN2_STORE_CODES}
              labels={GEN2_STORE_LABELS}
            />
            <RGDataColumn
              fieldName={'cntrMgmtType'}
              header={{ text: '용기관리유형' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={CNTR_MGMT_CODES}
              labels={CNTR_MGMT_LABELS}
            />
            <RGDataColumn
              fieldName={'posQtyRoundType'}
              header={{ text: 'POS수량 절사' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={POS_QTY_ROUND_CODES}
              labels={POS_QTY_ROUND_LABELS}
            />
            <RGDataColumn
              fieldName={'amtRoundType'}
              header={{ text: '금액 절사' }}
              width={130}
              editable={canEditInvoiceSetting}
              editor={{ type: 'dropdown', domainOnly: true }}
              lookupDisplay={true}
              values={AMT_ROUND_CODES}
              labels={AMT_ROUND_LABELS}
            />
            <RGDataColumn
              fieldName={'deptCd'}
              header={{ text: '귀속부서' }}
              width={100}
              editable={canEditInvoiceSetting}
              editor={{ type: 'line', maxLength: 20 }}
            />
            <RGDataColumn
              fieldName={'email'}
              header={{ text: 'E-mail' }}
              width={180}
              editable={canEditInvoiceSetting}
              editor={{ type: 'line', maxLength: 100 }}
            />
            <RGDataColumn
              fieldName={'emailRecipient'}
              header={{ text: 'E-mail 수신자' }}
              width={120}
              editable={canEditInvoiceSetting}
              editor={{ type: 'line', maxLength: 50 }}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
